<template>
  <Teleport to="body">
    <Transition name="paywall">
      <div v-if="authStore.showLimitPaywall" class="fixed inset-0 z-[120] flex items-end justify-center" @click.self="close">
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-black/40" @click="close" />

        <!-- Bottom sheet -->
        <div class="relative w-full max-w-[430px] bg-cream rounded-t-3xl px-6 pt-6 pb-sheet-8 z-10">
          <!-- Close button -->
          <button @click="close" class="absolute top-4 right-4 p-1">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#41291A" stroke-width="2.5" stroke-linecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>

          <!-- Zoya avatar -->
          <div class="flex justify-center mb-4">
            <img src="/Zoya_avatar_2_small.jpg" class="w-16 h-16 rounded-full object-cover" alt="Зоя" />
          </div>

          <!-- Приглашение, а не отказ. «На сегодня всё» било по человеку в тот
               момент, когда он и так упёрся, и читалось как наказание. Здесь
               то же событие подано как развилка: можно подождать, а можно
               продолжить прямо сейчас. -->
          <h2 class="text-lg font-bold text-center text-ink mb-2">Продолжим?</h2>
          <!-- Гость истратил три голосовых добавления (api/ai.js,
               GUEST_VOICE_LIMIT): ждать до утра бесполезно, попытки не
               возвращаются — поэтому без «вернётся в 5:00» -->
          <p v-if="voiceLimit" class="text-sm text-center text-ink/70 mb-2 leading-relaxed">
            В пробном режиме голосом можно добавить три блюда —
            попытки закончились.
          </p>
          <p v-else class="text-sm text-center text-ink/70 mb-2 leading-relaxed">
            Пробные токены на сегодня закончились — Зоя вернётся
            завтра в&nbsp;5:00.
          </p>
          <!-- Вариант B (services/sales.js): в iOS-сборке продаж нет — ни цен,
               ни «Выбрать тариф» (гайдлайн App Store 3.1.1), только факт лимита -->
          <!-- Подписка снимает дневной лимит целиком (checkTokenLimitFreeOnly),
               поэтому «не отключается» — правда и для голоса -->
          <p v-if="!iosSalesDisabled && voiceLimit" class="text-sm text-center text-ink mb-6 leading-relaxed">
            С подпиской голосовой ввод <b>не&nbsp;отключается вовсе</b> —
            надиктовывайте сколько нужно.
          </p>
          <!-- В iOS о подписке молчим: следующий шаг гостя — регистрация -->
          <p v-else-if="iosSalesDisabled && voiceLimit" class="text-sm text-center text-ink mb-6 leading-relaxed">
            Зарегистрируйтесь — это займёт меньше минуты.
          </p>
          <p v-else-if="!iosSalesDisabled" class="text-sm text-center text-ink mb-6 leading-relaxed">
            <!-- «В 10 раз» — округление, а не расчёт: при 25 000 и 200 000 это
                 ровно восемь. Разница между восемью и десятью на слух не
                 читается, а пропорцию примерно такой и держим. Если лимиты
                 разъедутся сильно, поправить надо здесь: константы в
                 middleware/tokenLimit.js. -->
            С подпиской лимит <b>в&nbsp;10 раз больше</b>, а&nbsp;<b>распознавание
            фото не&nbsp;отключается вовсе</b> — фотографируйте сколько нужно.
          </p>

          <!-- Button -->
          <button
            v-if="iosSalesDisabled && voiceLimit"
            @click="register"
            class="w-full py-3 rounded-xl text-sm font-medium text-white bg-ink active:bg-[#2a1a10] transition-colors"
          >
            Зарегистрироваться
          </button>
          <button
            v-else-if="iosSalesDisabled"
            @click="close"
            class="w-full py-3 rounded-xl text-sm font-medium text-white bg-ink active:bg-[#2a1a10] transition-colors"
          >
            Хорошо, подожду
          </button>
          <button
            v-else
            @click="goToSubscription"
            class="w-full py-3 rounded-xl text-sm font-medium text-white bg-ink active:bg-[#2a1a10] transition-colors"
          >
            Выбрать тариф
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, watch, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../store/auth.js'
import { track } from '../analytics.js'
import { reportPaywallEvent } from '../services/paywallEvents.js'
import { iosSalesDisabled } from '../services/sales.js'

const authStore = useAuthStore()
const router = useRouter()

// Во сколько раз подписка поднимает лимит. Оба числа приходят с сервера
// (/ai/token-budget), своей копии константы фронт не держит: она разъезжается
// после каждой правки middleware/tokenLimit.js, и обещание в пейволе
// перестаёт быть правдой незаметно.
let savedScrollY = 0

// Пейвол открыт из-за трёх голосовых добавлений гостя, а не дневного лимита
const voiceLimit = computed(() => authStore.limitPaywallReason === 'guest_voice')

// Блокировка скролла фона при открытии
watch(() => authStore.showLimitPaywall, (val) => {
  if (val) {
    reportPaywallEvent('limit', 'shown', { reason: authStore.limitPaywallReason })
    track('limit_paywall_shown', { pct: authStore.tokenBudget.pct, limit: authStore.tokenBudget.limit, reason: authStore.limitPaywallReason })
    savedScrollY = window.scrollY
    document.body.style.position = 'fixed'
    document.body.style.top = `-${savedScrollY}px`
    document.body.style.left = '0'
    document.body.style.right = '0'
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.left = ''
    document.body.style.right = ''
    document.body.style.overflow = ''
    window.scrollTo(0, savedScrollY)
    // Причину сбрасываем после закрытия: иначе следующий показ по дневному
    // лимиту (уже после регистрации) достался бы с текстом про голос
    authStore.limitPaywallReason = 'tokens'
  }
})

onUnmounted(() => {
  document.body.style.position = ''
  document.body.style.top = ''
  document.body.style.left = ''
  document.body.style.right = ''
  document.body.style.overflow = ''
})

function close() {
  reportPaywallEvent('limit', 'close')
  // Закрытие — половина воронки: без него не отличить «показали и не заинтересовал»
  // от «показали и человек ушёл платить».
  track('limit_paywall_dismissed', { pct: authStore.tokenBudget?.pct, reason: authStore.limitPaywallReason })
  authStore.showLimitPaywall = false
}

// Гость на странице тарифов при оплате сам попадёт в регистрацию
// (SubscriptionPage), поэтому «Выбрать тариф» годится и ему
function goToSubscription() {
  reportPaywallEvent('limit', 'cta')
  track('limit_paywall_subscribe_click', { pct: authStore.tokenBudget?.pct, reason: authStore.limitPaywallReason })
  authStore.showLimitPaywall = false
  router.push('/subscription')
}

// iOS-сборка: продаж нет, гостю после трёх голосовых — регистрация
function register() {
  reportPaywallEvent('limit', 'register')
  track('limit_paywall_register_click', { reason: authStore.limitPaywallReason })
  authStore.showLimitPaywall = false
  authStore.openAuthPopup()
}
</script>

<style scoped>
.paywall-enter-active,
.paywall-leave-active {
  transition: opacity 0.3s ease;
}
.paywall-enter-active .relative,
.paywall-leave-active .relative {
  transition: transform 0.3s ease;
}
.paywall-enter-from,
.paywall-leave-to {
  opacity: 0;
}
.paywall-enter-from .relative {
  transform: translateY(100%);
}
.paywall-leave-to .relative {
  transform: translateY(100%);
}
</style>
